import { MutationCache, QueryClient } from '@tanstack/react-query';
import { isImportLockingProject } from '@/atoms/import';
import { jotaiStore } from '@/atoms/store';
import { versionAtom } from '@/atoms/version';
import { ReadOnlyMutationError } from '@/hooks/useReadOnlyGate';

export const createOptimizedQueryClient = () => {
    return new QueryClient({
        mutationCache: new MutationCache({
            onMutate: () => {
                if (jotaiStore.get(versionAtom) !== '') {
                    throw new ReadOnlyMutationError();
                }
                // The import mutation itself passes this gate before it sets the
                // flag (the flag is raised inside its mutationFn). Only the project
                // actually being imported is blocked — an import elsewhere doesn't
                // freeze the project the user is currently editing.
                if (isImportLockingProject()) {
                    throw new ReadOnlyMutationError('Mutation blocked: project import in progress');
                }
            },
        }),
        defaultOptions: {
            queries: {
                // Stale time: how long until data is considered stale
                staleTime: 2 * 60 * 1000, // 2 minutes
                // GC time: how long until inactive data is garbage collected
                gcTime: 10 * 60 * 1000, // 10 minutes (was cacheTime)
                // Retry configuration
                retry: (failureCount, error) => {
                    // Don't retry on 4xx errors
                    if (error && typeof error === 'object' && 'status' in error) {
                        const status = (error as { status: number }).status;
                        if (status >= 400 && status < 500) {
                            return false;
                        }
                    }
                    return failureCount < 2;
                },
                retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
                // Refetch configuration
                refetchOnWindowFocus: false,
                refetchOnMount: false,
                refetchOnReconnect: 'always',
                // Network mode
                networkMode: 'online',
            },
            mutations: {
                retry: false,
                networkMode: 'online',
            },
        },
    });
};

// Query client instance
export const queryClient = createOptimizedQueryClient();
